import React, { useEffect, useState } from 'react';
import Has from '../../auth/Has';
import { CAN_MODIFY_SCENARIO } from '../../auth/permissions';

import './ScenarioVersions.scss';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SalvButton, SalvTab, SalvTabList, SalvTabPanel, SalvTabs, Size } from '@salvapp/ui-components';
import { useDispatch } from 'react-redux';
import { showFeedback } from '../../feedback/feedbackReducer';
import TestRuns from './scenario-testing/TestRuns';
import { showConfirmationModal } from '../../confirmation-modal/confirmationModalReducer';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { fetchTestRuns } from '../../common/versioning/helper';
import { InternalApiScenario } from '../../api-internal';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useDeleteScenarioVersion, useDuplicateScenarioVersion } from './scenarioVersionActions';
import ScenarioVersionsTable from './ScenarioVersionsTable';
import ScenarioVersionModal from './version-modal/ScenarioVersionModal';
import ScenarioVersionTestModal from './scenario-testing/ScenarioVersionTestModal';

const PARAM_TAB = 'tab';
const TEST_RUNS_TAB = 'test-runs';

type ScenarioVersionsProps = {
  scenario: InternalApiScenario;
};

const ScenarioVersions = ({ scenario }: ScenarioVersionsProps): React.ReactElement | null => {
  const [showVersionModal, setShowVersionModal] = useState(false);

  const [showTestModal, setShowTestModal] = useState(false);
  const [versionId, setVersionId] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get(PARAM_TAB) || 'versions';
  const [selectedTab, setSelectedTab] = useState(initialTab);

  const { id: scenarioId, versions, entityType } = scenario;
  const dispatch = useDispatch();

  const { mutate: duplicateScenarioVersion } = useDuplicateScenarioVersion();
  const { mutate: deleteScenarioVersion } = useDeleteScenarioVersion();

  const onComplete = async () => {
    setVersionId(null);
    setShowTestModal(false);
    setShowVersionModal(false);
  };

  const location = useLocation();

  useEffect(() => {
    searchParams.set(PARAM_TAB, selectedTab);
    setSearchParams(searchParams, {
      state: location.state,
      replace: true,
    });
  }, [selectedTab]);

  const createVersion = () => {
    setShowVersionModal(true);
    setVersionId(null);
  };
  const editVersion = (id: string) => {
    setShowVersionModal(true);
    setVersionId(id);
  };

  const testVersion = (versionId: string) => {
    setShowTestModal(true);
    setVersionId(versionId);
  };

  const duplicateToDraft = async (versionId: string) => {
    duplicateScenarioVersion({
      scenarioId: scenarioId,
      id: versionId,
    });

    setVersionId(null);
  };

  const deleteVersion = async (id: string) => {
    dispatch(
      showConfirmationModal({
        title: 'Are you sure?',
        description: 'This scenario version will be deleted.',
        confirmationText: 'Yes, delete it!',
        cancelText: 'No, keep it',
        onConfirm: async () => {
          try {
            deleteScenarioVersion({
              id,
              scenarioId,
            });

            await onComplete();
          } catch (_) {
            dispatch(
              showFeedback({
                message: 'Oops, something went wrong!',
                success: false,
              })
            );
          }
        },
      })
    );
  };

  const {
    data: testRuns,
    isFetching,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['scenarioTestRuns', scenarioId],
    queryFn: ({ pageParam }) => fetchTestRuns(scenarioId, pageParam),
    placeholderData: keepPreviousData,
    getNextPageParam: (prevData, allData, lastPageParam) => {
      return lastPageParam + 1;
    },
    initialPageParam: 0,
  });

  return (
    <div className='versions-container'>
      <SalvTabs value={selectedTab} onChange={(value) => setSelectedTab(value)}>
        <SalvTabList>
          <SalvTab value='versions' title={`Versions (${versions?.length || 0})`} />
          <SalvTab value='test-runs' title={`Test runs ${testRuns ? `(${testRuns?.pages[0].total})` : '(0)'}`} />
        </SalvTabList>
        <SalvTabPanel value='versions'>
          <div className='d-flex justify-content-end'>
            <Has allPermissions={CAN_MODIFY_SCENARIO}>
              <SalvButton
                variant='muted'
                size={Size.SMALL}
                onClick={createVersion}
                label={
                  <>
                    <FontAwesomeIcon icon={faPlus} className='me-2' />
                    New version
                  </>
                }
              />
            </Has>
          </div>
          <ScenarioVersionsTable
            versions={versions || []}
            entityType={entityType}
            isLibraryScenario={false}
            onDeleteVersion={deleteVersion}
            onEditVersion={editVersion}
            onDuplicateAsDraft={duplicateToDraft}
            onTestVersion={testVersion}
          />
        </SalvTabPanel>
        <SalvTabPanel value='test-runs'>
          <TestRuns
            data={testRuns?.pages.flatMap((page) => page.content) || []}
            isFetching={isFetching}
            fetchNextPage={fetchNextPage}
            total={testRuns?.pages[0].total || 0}
          />
        </SalvTabPanel>
      </SalvTabs>

      <ScenarioVersionModal
        showModal={showVersionModal}
        setShowModal={setShowVersionModal}
        versionId={versionId}
        scenario={scenario}
      />

      <ScenarioVersionTestModal
        scenario={scenario}
        show={showTestModal}
        versionId={versionId}
        onComplete={() => {
          setShowTestModal(false);
          refetch();
          setSelectedTab(TEST_RUNS_TAB);
        }}
        onClose={() => setShowTestModal(false)}
      />
    </div>
  );
};

export default ScenarioVersions;
